<template>
  <div class="verification-section">
    <h2 class="section-title">Verification Documents</h2>
    <div class="form-row">
      <div class="form-group">
        <label for="experienceLetter">Academic Certificate/Experience Letter <span class="required">*</span></label>
        <div v-if="!tempExperienceLetterUrl" class="file-upload-area" @click="triggerExperienceLetterFileInput" @dragover.prevent @drop.prevent="handleExperienceLetterFileDrop">
          <input id="experienceLetter" type="file" ref="experienceLetterInput" @change="handleExperienceLetterUpload" accept=".pdf,image/*" class="file-input" />
          <div class="upload-icon">
            <i class="fas fa-upload"></i>
          </div>
          <div class="upload-text">
            <span v-if="errors.certificateUrl" class="invalid-feedback" style="margin-top: -12px;">This field is required</span>
            Click to browse or drag and drop
            <div class="upload-format">Supported formats: PDF, IMAGE</div>
          </div>
          <div v-if="isUploadingExperienceLetter" class="loading-overlay">
            <div class="spinner"></div>
            <span>Uploading...</span>
          </div>
        </div>
        <div v-else class="file-preview">
          <div class="file-info">
            <img v-if="isExperienceLetterImage" :src="tempExperienceLetterUrl" alt="Experience Letter Image" class="preview-image" />
            <img v-else-if="isExperienceLetterPdf" :src="getDocumentThumbnail(tempExperienceLetterUrl)" alt="Document Preview" class="preview-image" />
          </div>
          <div class="action-button-container">
            <button type="button" class="view-btn" @click="openFileInNewTab(tempExperienceLetterUrl)">
              <i class="fas fa-eye"></i>
            </button>
            <button type="button" class="replace-btn" @click="triggerExperienceLetterFileInput" title="Replace Document">
              <i class="fas fa-sync-alt"></i>
            </button>
          </div>
          <input id="experienceLetterReplace" type="file" ref="experienceLetterInput" @change="handleExperienceLetterUpload" accept=".pdf,image/*" class="file-input" style="display:none;" />
        </div>
        <div class="certificate-note">
          For interns, it's required to submit an Academic Certificate; for experienced developers, submit an Experience Letter from your last organization.
        </div>
      </div>
      <div class="form-group">
        <label for="salarySlip">Salary Slip <span class="required">* </span></label>
        <div v-if="!tempSalarySlipUrl" class="file-upload-area" @click="triggerSalarySlipFileInput" @dragover.prevent @drop.prevent="handleSalarySlipFileDrop">
          <input id="salarySlip" type="file" ref="salarySlipInput" @change="handleSalarySlipUpload" accept=".pdf,image/*" class="file-input" />
        <div class="upload-icon">
            <i class="fas fa-upload"></i>
          </div>
          <div class="upload-text">
            <span v-if="errors.salarySlipUrl" class="invalid-feedback" style="margin-top: -12px;">This field is required</span>
            Click to browse or drag and drop
            <div class="upload-format">Supported formats: PDF, IMAGE</div>
          </div>
          <div v-if="isUploadingSalarySlip" class="loading-overlay">
            <div class="spinner"></div>
            <span>Uploading...</span>
          </div>
        </div>
        <div v-else class="file-preview">
          <div class="file-info">
            <img v-if="isSalarySlipImage" :src="tempSalarySlipUrl" alt="Salary Slip Image" class="preview-image" />
            <img v-else-if="isSalarySlipPdf" :src="getDocumentThumbnail(tempSalarySlipUrl)" alt="Document Preview" class="preview-image" />
          </div>
          <div class="action-button-container">
            <button type="button" class="view-btn" @click="openFileInNewTab(tempSalarySlipUrl)">
              <i class="fas fa-eye"></i>
            </button>
            <button type="button" class="replace-btn" @click="triggerSalarySlipFileInput" title="Replace Document">
              <i class="fas fa-sync-alt"></i>
            </button>
          </div>
          <input id="salarySlipReplace" type="file" ref="salarySlipInput" @change="handleSalarySlipUpload" accept=".pdf,image/*" class="file-input" style="display:none;" />
        </div>
      </div>
    </div>

    <div class="submit-button-container">
      <button type="button" class="button-submit" @click="submitDocuments" style="min-width: 200px">
       <span v-if="this.documentLoading">
        <img src="/static/img/loader.gif" alt="Loading" style="width:18px; height:18px;vertical-align:middle;" />
        </span>
        <span v-else>
          Submit Documents
        </span>
      </button>
    </div>
  </div>
</template>

<script>
import swal from "sweetalert";
import { openFileInNewTab } from '../../../../utils/helperFunc';

export default {
  name: 'VerifyDocuments',
  props: {
    user: { type: Object, required: true },
    isUploadingExperienceLetter: { type: Boolean, default: false },
    isUploadingSalarySlip: { type: Boolean, default: false },
    experienceLetterFileName: { type: String, default: '' },
    experienceLetterFileSize: { type: String, default: '' },
    experienceLetterFileType: { type: String, default: '' },
    salarySlipFileName: { type: String, default: '' },
    salarySlipFileSize: { type: String, default: '' },
    salarySlipFileType: { type: String, default: '' },
    tempExperienceLetterUrl: { type: String, default: '' },
    tempSalarySlipUrl: { type: String, default: '' },
    documentLoading: { type: Boolean, default: false },
  },
  data() {
    return {
      errors: {
        certificateUrl: '',
        salarySlipUrl: '',
      },
    };
  },
  computed: {
    isExperienceLetterImage() {
      return this.experienceLetterFileType && this.experienceLetterFileType.match('image.*');
    },
    isExperienceLetterPdf() {
      return this.experienceLetterFileType && this.experienceLetterFileType === 'application/pdf';
    },
    isSalarySlipImage() {
      return this.salarySlipFileType && this.salarySlipFileType.match('image.*');
    },
    isSalarySlipPdf() {
      return this.salarySlipFileType && this.salarySlipFileType === 'application/pdf';
    }
  },
  methods: {
    openFileInNewTab,
    inferFileType(url) {
      if (!url) return '';
      const extension = url.split('.').pop().toLowerCase().split('?')[0];
      if (['jpg', 'jpeg', 'png', 'gif'].includes(extension)) return 'image/' + extension;
      if (extension === 'pdf') return 'application/pdf';
      return '';
    },
    validateDocuments() {
      let isValid = true;
      const errors = {};

      if (!this.tempExperienceLetterUrl && !this.user.certificateUrl) {
        errors.certificateUrl = 'Academic Certificate/Experience Letter is required';
        isValid = false;
      }
      if (!this.tempSalarySlipUrl && !this.user.salarySlipUrl) {
        errors.salarySlipUrl = 'Salary Slip is required';
        isValid = false;
      }

      this.errors = errors;
      return isValid;
    },
    submitDocuments() {
      if (this.validateDocuments()) {
        this.$emit('submit-documents');
      } else {
        this.$nextTick(() => {
          const firstError = document.querySelector('.invalid-feedback');
          if (firstError) firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
        });
      }
    },
    triggerExperienceLetterFileInput() {
      this.$refs.experienceLetterInput.click();
    },
    triggerSalarySlipFileInput() {
      this.$refs.salarySlipInput.click();
    },
    handleExperienceLetterFileDrop(event) {
      const file = event.dataTransfer.files[0];
      if (file) {
        this.errors.certificateUrl = '';
        this.$emit('file-selected', file, 'experience_letter');
      }
    },
    handleSalarySlipFileDrop(event) {
      const file = event.dataTransfer.files[0];
      if (file) {
        this.errors.salarySlipUrl = '';
        this.$emit('file-selected', file, 'salary_slip');
      }
    },
    handleExperienceLetterUpload(event) {
      const file = event.target.files[0];
      if (file) {
        this.errors.certificateUrl = '';
        this.$emit('file-selected', file, 'experience_letter');
      }
      event.target.value = '';
    },
    handleSalarySlipUpload(event) {
      const file = event.target.files[0];
      if (file) {
        this.errors.salarySlipUrl = '';
        this.$emit('file-selected', file, 'salary_slip');
      }
      event.target.value = '';
    },
    getDocumentThumbnail(url) {
      if (!url || !url.includes('cloudinary.com')) return url;
      const lowerUrl = url.toLowerCase();
      if (lowerUrl.endsWith('.pdf')) {
        let normalized = url.replace('/raw/upload/', '/image/upload/').replace('/raw/', '/image/');
        const parts = normalized.split('/upload/');
        if (parts.length !== 2) return url;
        const transform = 'w_150,h_200,c_thumb,pg_1,f_auto,q_auto';
        return `${parts[0]}/upload/${transform}/${parts[1]}`;
      }

      return url;
    }
  }
};
</script>

<style scoped>
</style>


// WEBPACK FOOTER //
// src/components/Dashboard/Views/UserProfile/VerifyDocuments.vue